/* eslint-disable react/prop-types */
/**
 * Admin → Field Settings — edit labels, formats and visibility without a
 * release.
 *
 * Labels, display formats and field visibility are metadata rows, not code.
 * Until this screen existed, changing one meant an engineer writing SQL, a
 * migration applied by hand, and a deploy — for a decision about wording. See
 * Warwick-Investment-Group/wig-broker-deals#189.
 *
 * APP-AGNOSTIC BY CONSTRUCTION. The metadata table is per-app (it is coupled
 * to each app's schema), so what is shared is the CONTRACT, not the storage.
 * This screen never names a de-hub module or a database table: it asks the
 * server which tables exist and builds its form from
 * `EDITABLE_PROPERTIES`. Point `VITE_APP_API_BASE` at an app's module and it
 * works there. Anything that hard-codes a field name here is a bug.
 *
 * TWO THINGS THIS SCREEN MUST KEEP SAYING OUT LOUD
 *
 * 1. A LABEL IS ALSO AN UPLOAD-WORKBOOK COLUMN HEADER. Renaming one changes
 *    the header of every template downloaded afterwards. Workbooks already
 *    filled in keep working — the server records the old label and still
 *    matches it — but the person doing the renaming should know that is what
 *    is happening, which is why the confirm step spells it out rather than
 *    asking "Are you sure?".
 *
 * 2. "EDITABLE" IS A DISPLAY SETTING, NOT A PERMISSION. It draws the field as
 *    an input instead of read-only. Who may actually write it is decided by
 *    their role on the server. A checkbox labelled "Editable" invites the
 *    opposite reading, so the help text corrects it in place.
 *
 * Gated on the app's configured admin role; de-hub's own role check is the
 * real boundary and 403s everyone else.
 */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { HistoryOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";

import { WigAntTable } from "@warwick-investment-group/wig-react-library";

import SignIn from "../../signIn";
import { AppContext } from "../../../AppContextProvider";
import useIsAdmin from "../../../hooks/useIsAdmin";
import metadataAdminService from "../../../services/metadataAdminService";
import {
  EDITABLE_PROPERTIES,
  IS_UNCONFIGURED,
  METADATA_SOURCES,
  DEFAULT_SOURCE_KEY,
  sourceByKey,
} from "../../../config/metadataAdmin";

const { Title, Text, Paragraph } = Typography;

/** Properties whose value is worth showing in the grid at a glance. */
const SUMMARY_COLUMNS = ["label", "format", "category", "ord"];

const fmtDateTime = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const MetadataAdmin = ({ darkMode }) => {
  const navigate = useNavigate();
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const isAdmin = useIsAdmin();

  // Which metadata table is being edited. The app's own and the shared
  // App Support one live in different de-hub modules and databases, and both
  // implement the same contract — see METADATA_SOURCES.
  const [sourceKey, setSourceKey] = useState(DEFAULT_SOURCE_KEY);
  const source = sourceByKey(sourceKey);

  const [tables, setTables] = useState([]);
  const [tableName, setTableName] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Same redirect the other Admin screens use. The server is the boundary;
  // this only avoids drawing controls that would 403.
  useEffect(() => {
    if (!isAdmin) navigate("/");
  }, [isAdmin, navigate]);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const loadTables = useCallback(async () => {
    if (IS_UNCONFIGURED) return;
    try {
      const { token: tk, userOid } = await getAuth();
      const resp = await metadataAdminService.listTables({
        token: tk,
        userOid,
        source,
      });
      const list = resp?.data?.data || [];
      setTables(list);
      // Not `cur ?? list[0]`: switching source has to move off the old
      // source's table, which will not be in this list.
      setTableName((cur) => (cur && list.includes(cur) ? cur : list[0] ?? null));
    } catch (err) {
      console.error("Failed to load metadata tables:", err?.message || err);
      setErrorMsg("Couldn't load the list of tables.");
    }
  }, [getAuth, source]);

  const loadRows = useCallback(async () => {
    if (!tableName) return;
    try {
      setLoading(true);
      setErrorMsg(null);
      const { token: tk, userOid } = await getAuth();
      const resp = await metadataAdminService.listMetadata({
        token: tk,
        userOid,
        tableName,
        source,
      });
      setRows(resp?.data?.data || []);
    } catch (err) {
      console.error("Failed to load metadata:", err?.message || err);
      setErrorMsg("Couldn't load the fields for that table.");
    } finally {
      setLoading(false);
    }
  }, [getAuth, tableName, source]);

  useEffect(() => {
    loadTables();
  }, [loadTables]);
  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openEditor = useCallback(
    (row) => {
      setEditing(row);
      form.setFieldsValue(
        EDITABLE_PROPERTIES.reduce(
          (acc, p) => ({ ...acc, [p.name]: row[p.name] ?? undefined }),
          {}
        )
      );
    },
    [form]
  );

  /**
   * Save only what moved.
   *
   * Sending the whole form would audit every property on every save, which
   * turns the change log into noise and makes "who renamed this?"
   * unanswerable — the question the log exists for.
   */
  const handleSave = useCallback(async () => {
    if (!editing) return;
    let values;
    try {
      values = await form.validateFields();
    } catch {
      // Rejected because a rule failed. antd has already marked the field and
      // shown the message, so the only thing left to do is not save.
      return;
    }
    const updates = {};
    for (const p of EDITABLE_PROPERTIES) {
      const next = values[p.name];
      const before = editing[p.name];
      // A cleared text box is `undefined` from antd but means "null" here.
      const normalized =
        next === undefined || next === "" ? null : next;
      const prior = before === undefined || before === "" ? null : before;
      if (normalized !== prior) updates[p.name] = normalized;
    }
    if (Object.keys(updates).length === 0) {
      setEditing(null);
      return;
    }
    try {
      setSaving(true);
      const { token: tk, userOid } = await getAuth();
      await metadataAdminService.updateMetadata({
        token: tk,
        userOid,
        id: editing.id,
        updates,
        source,
      });
      message.success("Saved.");
      setEditing(null);
      loadRows();
    } catch (err) {
      console.error("Failed to save metadata:", err?.message || err);
      message.error(
        err?.response?.data?.detail || "Couldn't save that change."
      );
    } finally {
      setSaving(false);
    }
  }, [editing, form, getAuth, loadRows, source]);

  const openAudit = useCallback(async () => {
    setAuditOpen(true);
    try {
      setAuditLoading(true);
      const { token: tk, userOid } = await getAuth();
      const resp = await metadataAdminService.listAudit({
        token: tk,
        userOid,
        tableName,
        limit: 200,
        source,
      });
      setAudit(resp?.data?.data || []);
    } catch (err) {
      console.error("Failed to load the change log:", err?.message || err);
      message.error("Couldn't load the change log.");
    } finally {
      setAuditLoading(false);
    }
  }, [getAuth, tableName, source]);

  const columns = useMemo(
    () => [
      {
        title: "Field",
        dataIndex: "field",
        key: "field",
        width: 200,
        sorter: (a, b) =>
          String(a?.field ?? "").localeCompare(String(b?.field ?? "")),
        render: (v, row) => (
          <Space direction="vertical" size={0}>
            <Text code>{v}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {row.data_type}
            </Text>
          </Space>
        ),
      },
      ...SUMMARY_COLUMNS.map((name) => ({
        title:
          EDITABLE_PROPERTIES.find((p) => p.name === name)?.label ?? name,
        dataIndex: name,
        key: name,
        // `ord` is the display position, so it has to sort as a number —
        // the rest are text.
        sorter:
          name === "ord"
            ? (a, b) => Number(a?.ord ?? 0) - Number(b?.ord ?? 0)
            : (a, b) =>
                String(a?.[name] ?? "").localeCompare(String(b?.[name] ?? "")),
        render: (v) =>
          v === null || v === undefined || v === "" ? (
            <Text type="secondary">—</Text>
          ) : (
            String(v)
          ),
      })),
      {
        title: "Shown",
        dataIndex: "hide",
        key: "hide",
        width: 90,
        sorter: (a, b) => Number(Boolean(a?.hide)) - Number(Boolean(b?.hide)),
        render: (v) =>
          v ? <Tag>Hidden</Tag> : <Tag color="green">Shown</Tag>,
      },
      {
        title: "Filter",
        dataIndex: "filterable",
        key: "filterable",
        width: 100,
        // Three states sort as Never < Default < Always, so the two columns an
        // admin has actually ruled on sit at the ends of the sort rather than
        // scattered through the middle.
        sorter: (a, b) =>
          (a?.filterable === true ? 1 : a?.filterable === false ? -1 : 0) -
          (b?.filterable === true ? 1 : b?.filterable === false ? -1 : 0),
        render: (v) =>
          v === true ? (
            <Tag color="green">Always</Tag>
          ) : v === false ? (
            <Tag>Never</Tag>
          ) : (
            <Text type="secondary">Default</Text>
          ),
      },
      {
        title: "",
        key: "_edit",
        width: 80,
        render: (_v, row) => (
          <Button size="small" onClick={() => openEditor(row)}>
            Edit
          </Button>
        ),
      },
    ],
    [openEditor]
  );

  const content = (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        Field Settings
      </Title>
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        What each field is called, how its numbers are shown, and where it
        appears. Changes take effect for everyone the next time a screen
        loads — no new version of the app is needed.
      </Paragraph>

      {IS_UNCONFIGURED && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="This app hasn't been pointed at its API yet"
          description={
            <>
              Set <Text code>VITE_APP_API_BASE</Text> to this app&apos;s de-hub
              module path (for example <Text code>/broker_deals</Text>) and
              rebuild. Until then this screen has nothing to read.
            </>
          }
        />
      )}

      {errorMsg && (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          message={errorMsg}
          onClose={() => setErrorMsg(null)}
        />
      )}

      <Space style={{ marginBottom: 12 }} wrap>
        {METADATA_SOURCES.length > 1 && (
          <Select
            style={{ minWidth: 200 }}
            value={sourceKey}
            onChange={setSourceKey}
            options={METADATA_SOURCES.map((s) => ({
              value: s.key,
              label: s.label,
            }))}
            disabled={IS_UNCONFIGURED}
            aria-label="Metadata source"
          />
        )}
        <Select
          style={{ minWidth: 240 }}
          placeholder="Choose a table"
          value={tableName ?? undefined}
          onChange={setTableName}
          options={tables.map((t) => ({ value: t, label: t }))}
          disabled={IS_UNCONFIGURED}
          aria-label="Table"
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={loadRows}
          disabled={!tableName}
          aria-label="Refresh"
        >
          Refresh
        </Button>
        <Button
          icon={<HistoryOutlined />}
          onClick={openAudit}
          disabled={IS_UNCONFIGURED}
        >
          Change log
        </Button>
      </Space>

      <WigAntTable
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        showSearch={false}
        showExport={false}
        showActionButton={false}
        pagination={{ defaultPageSize: 100, showSizeChanger: true }}
        locale={{
          emptyText: (
            <Empty description="No fields to show for this table." />
          ),
        }}
      />

      <Drawer
        title={
          editing ? `${editing.table_name}.${editing.field}` : "Edit field"
        }
        width={520}
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        extra={
          <Space>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>
          </Space>
        }
      >
        {/* Said once, here, where the rename actually happens. The person
            doing it is the one who needs to know — not the reviewer of a PR
            they will never see. */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Renaming a field changes the upload template"
          description={
            "The label is also the column header in the upload workbook. " +
            "Workbooks people have already filled in keep working — the old " +
            "name is remembered — but templates downloaded from now on will " +
            "use the new one."
          }
        />
        <Form form={form} layout="vertical">
          {EDITABLE_PROPERTIES.map((p) => (
            <Form.Item
              key={p.name}
              name={p.name}
              label={p.label}
              extra={p.help}
              valuePropName={p.type === "boolean" ? "checked" : "value"}
              rules={
                p.name === "label"
                  ? [
                      {
                        required: true,
                        whitespace: true,
                        message:
                          "A field needs a label — it is what users see.",
                      },
                    ]
                  : undefined
              }
            >
              {p.type === "boolean" ? (
                <Switch />
              ) : p.type === "select" ? (
                <Select allowClear options={p.options} />
              ) : p.type === "number" ? (
                <InputNumber style={{ width: "100%" }} />
              ) : p.type === "textarea" ? (
                <Input.TextArea rows={3} />
              ) : (
                <Input />
              )}
            </Form.Item>
          ))}
        </Form>
      </Drawer>

      <Drawer
        title="Change log"
        width={640}
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
      >
        <WigAntTable
          rowKey="id"
          size="small"
          loading={auditLoading}
          dataSource={audit}
          showSearch={false}
          showExport={false}
          showActionButton={false}
          pagination={{ pageSize: 25 }}
          locale={{ emptyText: <Empty description="Nothing changed yet." /> }}
          columns={[
            {
              title: "Field",
              key: "field",
              // Sorts on what the cell shows: table.field.
              sorter: (a, b) =>
                `${a?.table_name ?? ""}.${a?.field ?? ""}`.localeCompare(
                  `${b?.table_name ?? ""}.${b?.field ?? ""}`
                ),
              render: (_v, r) => (
                <Text code style={{ fontSize: 11 }}>
                  {r.table_name}.{r.field}
                </Text>
              ),
            },
            {
              title: "Property",
              dataIndex: "property",
              key: "property",
              sorter: (a, b) =>
                String(a?.property ?? "").localeCompare(
                  String(b?.property ?? "")
                ),
            },
            {
              title: "Change",
              key: "change",
              // The cell is "old → new"; the new value is what a reader is
              // looking for, so that is what this orders on.
              sorter: (a, b) =>
                String(a?.new_value ?? "").localeCompare(
                  String(b?.new_value ?? "")
                ),
              render: (_v, r) => (
                <Space size={4}>
                  <Text delete type="secondary">
                    {r.old_value ?? "—"}
                  </Text>
                  <Text>→</Text>
                  <Text strong>{r.new_value ?? "—"}</Text>
                </Space>
              ),
            },
            {
              title: "By",
              key: "by",
              sorter: (a, b) =>
                String(a?.changed_by ?? "").localeCompare(
                  String(b?.changed_by ?? "")
                ),
              render: (_v, r) => (
                <Tooltip title={fmtDateTime(r.changed_at)}>
                  <Text style={{ fontSize: 11 }}>{r.changed_by}</Text>
                </Tooltip>
              ),
            },
          ]}
        />
      </Drawer>
    </div>
  );

  return (
    <>
      <AuthenticatedTemplate>{content}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn darkMode={darkMode} />
      </UnauthenticatedTemplate>
    </>
  );
};

export default MetadataAdmin;
